import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, RotateCcw, Send, Check } from "lucide-react";

const modeles = [
  { id: "forstyl", name: "Forstyl S", certif: "A2P BP1", price: "à partir de 3 490 €" },
  { id: "protecta", name: "Protecta", certif: "A2P BP2", price: "à partir de 4 890 €" },
  { id: "spheris", name: "Spheris", certif: "A2P BP3", price: "à partir de 6 290 €" },
];

const couleurs = [
  { id: "noir", name: "Noir profond", hex: "#141414" },
  { id: "anthracite", name: "Anthracite", hex: "#3a3a3a" },
  { id: "blanc", name: "Blanc pur", hex: "#f5f5f5" },
  { id: "chene", name: "Chêne naturel", hex: "#b08050" },
  { id: "noyer", name: "Noyer", hex: "#5c3a21" },
  { id: "bordeaux", name: "Bordeaux", hex: "#5c1a2b" },
  { id: "bleu", name: "Bleu nuit", hex: "#1F3A5F" },
  { id: "vert", name: "Vert forêt", hex: "#3F5A36" },
];

const poignees = [
  { id: "barre", name: "Barre verticale" },
  { id: "classique", name: "Classique horizontale" },
  { id: "design", name: "Design minimaliste" },
];

const formes = [
  { id: "pleine", name: "Pleine" },
  { id: "vitre-haut", name: "Vitrage haut" },
  { id: "vitre-lateral", name: "Vitrage latéral" },
];

const Simulateur = () => {
  const [config, setConfig] = useState({
    modele: "protecta",
    couleur: "anthracite",
    poignee: "barre",
    forme: "pleine",
  });

  const selectedColor = useMemo(() => couleurs.find((c) => c.id === config.couleur), [config.couleur]);
  const selectedModele = useMemo(() => modeles.find((m) => m.id === config.modele), [config.modele]);

  const isLight = ["blanc", "chene"].includes(config.couleur);
  const handleColor = isLight ? "#0D2B21" : "#f5f5f5";

  const reset = () => setConfig({ modele: "protecta", couleur: "anthracite", poignee: "barre", forme: "pleine" });

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-[#123B2E] text-white py-14">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-[#C9A227] transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Simulateur de porte</span>
          </nav>
          <div className="inline-block bg-[#C9A227] text-black text-xs font-bold tracking-widest uppercase px-3 py-1.5 mb-4">Configurateur 3D</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-tight max-w-3xl">Configurez votre nouvelle porte blindée Fichet</h1>
          <div className="inline-flex items-center gap-2 bg-[#C9A227] text-black text-sm font-bold px-4 py-2 mt-4">
            Dépannage express dans toute la commune
          </div>
          <p className="text-white/80 mt-3 max-w-2xl">Choisissez le modèle, la couleur et les accessoires. Visualisez le rendu en temps réel.</p>
        </div>
      </div>

      {/* Configurator */}
      <div className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-[1fr_420px] gap-8">
          {/* Preview */}
          <div className="bg-white p-6 md:p-10 relative overflow-hidden">
            <div className="absolute top-6 right-6 z-10">
              <button onClick={reset} data-testid="sim-reset-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-black transition-colors">
                <RotateCcw className="w-4 h-4" /> Réinitialiser
              </button>
            </div>

            <div className="aspect-[3/4] max-h-[640px] mx-auto flex items-center justify-center relative" style={{ maxWidth: "420px" }}>
              {/* Wall/frame */}
              <div className="absolute inset-0 bg-gradient-to-b from-stone-100 to-stone-200" />
              <div className="absolute inset-x-6 top-6 bottom-0 bg-stone-300 shadow-inner" />

              {/* Door frame */}
              <div className="relative w-[75%] h-[95%] shadow-2xl transition-all duration-500" style={{ backgroundColor: "#2a2a2a", padding: "8px" }}>
                {/* Door */}
                <div
                  className="w-full h-full relative transition-all duration-500"
                  style={{ backgroundColor: selectedColor.hex }}
                >
                  {/* Panel details */}
                  <div
                    className="absolute inset-4 border-2 transition-all duration-500"
                    style={{ borderColor: isLight ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.12)" }}
                  />
                  <div
                    className="absolute top-8 left-8 right-8 h-[35%] border transition-all duration-500"
                    style={{ borderColor: isLight ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.08)" }}
                  />
                  <div
                    className="absolute bottom-8 left-8 right-8 h-[35%] border transition-all duration-500"
                    style={{ borderColor: isLight ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.08)" }}
                  />

                  {/* Glass panel */}
                  {config.forme === "vitre-haut" && (
                    <div className="absolute top-6 left-6 right-6 h-[20%] bg-gradient-to-b from-sky-100/60 to-sky-200/60 backdrop-blur-sm" />
                  )}
                  {config.forme === "vitre-lateral" && (
                    <div className="absolute top-6 bottom-6 right-6 w-[15%] bg-gradient-to-b from-sky-100/60 to-sky-200/60 backdrop-blur-sm" />
                  )}

                  {/* Handle */}
                  {config.poignee === "barre" && (
                    <div className="absolute right-4 top-[15%] bottom-[15%] w-2 rounded-full shadow-md transition-all duration-300" style={{ backgroundColor: handleColor }} />
                  )}
                  {config.poignee === "classique" && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-2 rounded-full shadow-md" style={{ backgroundColor: handleColor }} />
                  )}
                  {config.poignee === "design" && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-14 shadow-md" style={{ backgroundColor: handleColor }} />
                  )}

                  {/* Lock */}
                  <div className="absolute right-4 top-[52%] w-3 h-3 rounded-full shadow-md" style={{ backgroundColor: handleColor }} />

                  {/* Fichet logo plate */}
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest" style={{ color: isLight ? "#666" : "#999" }}>
                    FICHET
                  </div>
                </div>
              </div>
            </div>

            {/* Preview info */}
            <div className="mt-6 text-center text-sm text-gray-500">Rendu à titre indicatif — finitions réelles présentées en showroom</div>
          </div>

          {/* Options */}
          <div className="space-y-6">
            {/* Modele */}
            <div className="bg-white p-6">
              <div className="text-xs font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Étape 1 • Modèle</div>
              <div className="space-y-2">
                {modeles.map((m) => (
                  <button key={m.id} onClick={() => setConfig({ ...config, modele: m.id })} data-testid={`sim-modele-${m.id}`}
                    className={`w-full flex items-center justify-between p-4 border-2 text-left transition-all ${
                      config.modele === m.id ? "border-[#C9A227] bg-[#C9A227]/10" : "border-gray-200 hover:border-gray-400"
                    }`}>
                    <div>
                      <div className="font-bold text-black">{m.name}</div>
                      <div className="text-xs text-gray-500">{m.certif} • {m.price}</div>
                    </div>
                    {config.modele === m.id && (
                      <div className="w-6 h-6 bg-[#C9A227] flex items-center justify-center">
                        <Check className="w-4 h-4 text-black" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Couleur */}
            <div className="bg-white p-6">
              <div className="text-xs font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Étape 2 • Couleur</div>
              <div className="grid grid-cols-4 gap-2">
                {couleurs.map((c) => (
                  <button key={c.id} onClick={() => setConfig({ ...config, couleur: c.id })} title={c.name} data-testid={`sim-couleur-${c.id}`}
                    className={`aspect-square border-2 relative transition-all ${
                      config.couleur === c.id ? "border-black scale-105" : "border-transparent hover:border-gray-400"
                    }`}
                    style={{ backgroundColor: c.hex }}>
                    {config.couleur === c.id && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="text-sm text-gray-600 mt-3">Couleur : <span className="font-bold text-black">{selectedColor.name}</span></div>
            </div>

            {/* Forme */}
            <div className="bg-white p-6">
              <div className="text-xs font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Étape 3 • Vitrage</div>
              <div className="grid grid-cols-3 gap-2">
                {formes.map((f) => (
                  <button key={f.id} onClick={() => setConfig({ ...config, forme: f.id })} data-testid={`sim-forme-${f.id}`}
                    className={`p-3 border-2 text-sm font-semibold transition-all ${
                      config.forme === f.id ? "border-[#C9A227] bg-[#C9A227]/10 text-black" : "border-gray-200 hover:border-gray-400 text-gray-700"
                    }`}>
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Poignée */}
            <div className="bg-white p-6">
              <div className="text-xs font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Étape 4 • Poignée</div>
              <div className="space-y-2">
                {poignees.map((p) => (
                  <button key={p.id} onClick={() => setConfig({ ...config, poignee: p.id })} data-testid={`sim-poignee-${p.id}`}
                    className={`w-full flex items-center justify-between p-3 border-2 text-left transition-all ${
                      config.poignee === p.id ? "border-[#C9A227] bg-[#C9A227]/10" : "border-gray-200 hover:border-gray-400"
                    }`}>
                    <span className="font-semibold text-black">{p.name}</span>
                    {config.poignee === p.id && <Check className="w-4 h-4 text-black" strokeWidth={3} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary + CTA */}
            <div className="bg-[#123B2E] text-white p-6">
              <div className="text-xs font-bold text-[#C9A227] tracking-widest uppercase mb-3">Votre configuration</div>
              <div data-testid="sim-summary-modele" className="text-2xl font-black tracking-tight mb-1">Fichet {selectedModele.name}</div>
              <div className="text-sm text-white/70 mb-4">{selectedModele.certif} • {selectedColor.name}</div>
              <div className="text-lg font-bold text-[#C9A227] mb-5">{selectedModele.price}</div>
              <Link to="/devis" className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#A8861C] text-black font-bold px-5 py-4 transition-colors">
                <Send className="w-4 h-4" /> Demander un devis
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Simulateur;
